import { Link } from "react-router";
import type { StepGate } from "../api/types";
import { BackButton } from "./BackButton";
import { Button } from "./Button";

interface GateFooterProps {
  gate: StepGate;
  done: boolean;
  label: string;
  busy?: boolean;
  onComplete: () => void;
  /** Where to go once this step is done, e.g. { to: "/gig/5", label: "Continue to Step 5" }. */
  next?: { to: string; label: string } | undefined;
}

/**
 * Back + Complete, with a plain list of what's still missing. The button only enables when the server's gate passes.
 *
 * Once the step is done the primary action becomes "Continue to the next step". Re-saving a finished step is still
 * possible but secondary: it used to be the only button ("Saved. Update step"), so people kept re-completing a step
 * (Step 4 sent them back to the Launch Plan each time) and never found the way forward.
 */
export function GateFooter({ gate, done, label, busy, onComplete, next }: GateFooterProps) {
  const missing = gate.items.filter((item) => !item.done).map((item) => item.label);

  if (done) {
    return (
      <div className="mt-6">
        <div className="flex flex-wrap gap-2.5">
          <BackButton to="/hub" />
          {next ? (
            <Link
              to={next.to}
              className="inline-flex min-h-11 items-center justify-center rounded-[var(--radius-control)] bg-accent px-[22px] text-[14.5px] font-bold text-on-accent hover:bg-accent-pressed"
            >
              {next.label} →
            </Link>
          ) : null}
          <Button variant="ghost" onClick={onComplete} disabled={!gate.pass || busy}>
            Save my changes
          </Button>
        </div>
        <p className="mt-2.5 text-[13px] text-positive">
          ✓ This step is complete. You can still edit it.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-6">
      <div className="flex flex-wrap gap-2.5">
        <BackButton to="/hub" />
        <Button onClick={onComplete} disabled={!gate.pass || busy}>
          {label} →
        </Button>
      </div>
      {missing.length ? (
        <p className="mt-2.5 text-[13px] text-text-muted">Still needed: {missing.join(", ")}.</p>
      ) : null}
    </div>
  );
}
